import { useCallback } from "react";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import type {
  RootState,
  AppDispatch,
  ComponentSelectors,
  ComponentActions,
  UsedAppProps,
  UsedAppCallbacks,
  UseAppStateHook,
} from "./types";

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

const useAppProps = function <T extends ComponentSelectors>(
  componentSelectors: T,
): UsedAppProps<T> {
  const getProp = useAppSelector;
  return Object.keys(componentSelectors).reduce(
    (props, propName) => ({
      ...props,
      [propName]: getProp(componentSelectors[propName]),
    }),
    {} as UsedAppProps<T>,
  );
};

const useAppCallbacks = function <T extends ComponentActions>(
  componentActions: T,
): UsedAppCallbacks<T> {
  const dispatch = useDispatch<AppDispatch>();
  const memo = useCallback;
  return Object.keys(componentActions).reduce(
    (callbacks, callbackName) => ({
      ...callbacks,
      [callbackName]: memo(
        (...args: unknown[]) =>
          dispatch(componentActions[callbackName](...args)),
        [callbackName],
      ),
    }),
    {} as UsedAppCallbacks<T>,
  );
};

export const useAppState: UseAppStateHook = function <
  S extends ComponentSelectors,
  A extends ComponentActions,
>(componentRequirements: {
  selectors?: S;
  actions?: A;
}):
  | UsedAppProps<S>
  | UsedAppCallbacks<A>
  | (UsedAppProps<S> & UsedAppCallbacks<A>) {
  const { selectors = {}, actions = {} } = componentRequirements;
  const nextAppState = {
    ...useAppProps(selectors),
    ...useAppCallbacks(actions),
  };
  if (selectors && actions)
    return nextAppState as UsedAppProps<S> & UsedAppCallbacks<A>;
  if (selectors) return nextAppState as UsedAppProps<S>;
  return nextAppState as UsedAppCallbacks<A>;
};

export const useAppDispatch = () => useDispatch<AppDispatch>();
